import { Button, FormGroup, Radio, RadioGroup, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '../../../utils/validation';
import type { IRegistrationCodes } from '../../interfaces/registrationCodes';
import { translateKey } from '../../../translate';
import { getSupervisors } from '../../../utils/apis/getSupervisors';
import {
    isIdentifierDeclarationOrSupervisorRequired,
    isIdentifierDeclarationRequired,
    isIdentifierRegistrationBigAllowed,
} from '../../../utils/practitionerIdentifiersHelpers';
import { FormContextProvider } from '../../elements/formContext';
import { Modal } from '../../elements/modals/Modal';
import { SelectSupervisor } from '../../elements/SelectSupervisor';
import { CancelButton } from '../../userDataManagement/CancelButton';
import { IdentifierDeclaration } from '../../userDataManagement/IdentifierDeclaration';
import { InputTextField } from '../../userDataManagement/InputText';

type IdentifierDeclarationType = 'identifierDeclaration' | 'selectSupervisor';

export interface IPersonalSettingsAddRegistrationCodesModalProps {
    errors: ValidationErrors;
    identifiers: IRegistrationCodes;
    onCancel: () => void;
    onChange: <K extends keyof IRegistrationCodes>(name: K, value: IRegistrationCodes[K]) => void;
    onSave: () => Promise<void>;
    onValidateOne: <K extends keyof IRegistrationCodes>(name: K) => void;
    organizationId: string;
    practitionerId: string;
}

export function PersonalSettingsAddRegistrationCodesModal(props: IPersonalSettingsAddRegistrationCodesModalProps) {
    const [identifierDeclarationType, setIdentifierDeclarationType] =
        useState<IdentifierDeclarationType>('identifierDeclaration');
    const [addIdentifierDeclaration, setAddIdentifierDeclaration] = useState(false);
    const [addIdentifierDeclarationOrSupervisor, setAddIdentifierDeclarationOrSupervisor] = useState(false);
    const [addIdentifierRegistrationBig, setAddIdentifierRegistrationBig] = useState(false);
    const [supervisorsAvailable, setSupervisorsAvailable] = useState(false);
    const [selectedSupervisorLabel, setSelectedSupervisorLabel] = useState<string | null>(null);

    useEffect(() => {
        loadSupervisors();
    }, []);

    useEffect(() => {
        if (props.identifiers.profession) {
            setAddIdentifierDeclaration(isIdentifierDeclarationRequired([props.identifiers.profession]));
            setAddIdentifierDeclarationOrSupervisor(
                isIdentifierDeclarationOrSupervisorRequired([props.identifiers.profession])
            );
            setAddIdentifierRegistrationBig(isIdentifierRegistrationBigAllowed([props.identifiers.profession]));
        }
    }, [props.identifiers.profession]);

    if (!props.identifiers.profession) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'identifiers',
                item: props.identifiers,
                errors: props.errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => props.onValidateOne(key),
            }}
        >
            <Modal
                actionButtons={[
                    <CancelButton
                        disabled={false}
                        onCancel={async () => await props.onCancel()}
                    />,
                    <Button
                        onClick={async () => await handleSubmit()}
                        type="button"
                        variant="outline"
                    >
                        {translateKey('personalSettings.save')}
                    </Button>,
                ]}
                isOpen
                onClose={async () => await props.onCancel()}
                size="medium"
                title={getTitle()}
            >
                <Text>{getHeader() + ' ' + getAddToProfileExplanation()}</Text>

                {renderIdentifierDeclaration()}
                {renderIdentifierRegistrationBig()}
            </Modal>
        </FormContextProvider>
    );

    function getTitle() {
        if (addIdentifierRegistrationBig) {
            if (addIdentifierDeclarationOrSupervisor || addIdentifierDeclaration) {
                return translateKey(
                    'personalSettings.addIdentifier',
                    translateKey(
                        'identifierDeclarationOrRegistration.identifierDeclarationAndIdentifierRegistrationBig'
                    )
                );
            } else {
                return translateKey(
                    'personalSettings.addIdentifier',
                    translateKey('identifierDeclarationOrRegistration.identifierRegistrationBig')
                );
            }
        } else {
            if (addIdentifierDeclarationOrSupervisor || addIdentifierDeclaration) {
                return translateKey('personalSettings.addIdentifier', translateKey('organization.agbCode'));
            } else {
                return '';
            }
        }
    }

    function getHeader() {
        if (addIdentifierRegistrationBig && (addIdentifierDeclarationOrSupervisor || addIdentifierDeclaration)) {
            return (
                translateKey('identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession') +
                ' ' +
                translateKey('identifierDeclarationOrRegistration.enterValueToContinue')
            );
        } else {
            if (addIdentifierDeclarationOrSupervisor) {
                return (
                    translateKey(
                        'identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession'
                    ) +
                    ' ' +
                    translateKey(
                        supervisorsAvailable
                            ? 'identifierDeclarationOrRegistration.addIdentifierDeclarationOrSupervisorToContinue'
                            : 'identifierDeclarationOrRegistration.addIdentifierDeclarationToContinue'
                    )
                );
            } else if (addIdentifierDeclaration) {
                return (
                    translateKey(
                        'identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession'
                    ) +
                    ' ' +
                    translateKey('identifierDeclarationOrRegistration.addIdentifierDeclarationToContinue')
                );
            } else {
                return ''; // We should not come here.
            }
        }
    }

    function getAddToProfileExplanation() {
        let identifierSavedIntoPractitionerProfileTranslateKey =
            'identifierDeclarationOrRegistration.identifierSavedIntoPractitionerProfile';
        let identifierTranslation: string | null = null;
        if (addIdentifierRegistrationBig) {
            if (addIdentifierDeclarationOrSupervisor || addIdentifierDeclaration) {
                identifierSavedIntoPractitionerProfileTranslateKey =
                    'identifierDeclarationOrRegistration.identifierSavedIntoPractitionerProfilePlural';
                identifierTranslation = translateKey(
                    'identifierDeclarationOrRegistration.identifierDeclarationAndIdentifierRegistrationBig'
                );
            } else {
                identifierTranslation = translateKey('identifierDeclarationOrRegistration.identifierRegistrationBig');
            }
        } else {
            if (addIdentifierDeclarationOrSupervisor || addIdentifierDeclaration) {
                identifierTranslation = translateKey('practitioner.identifierDeclaration');
            }
        }

        return identifierTranslation
            ? translateKey(identifierSavedIntoPractitionerProfileTranslateKey, identifierTranslation)
            : '';
    }

    function renderIdentifierDeclaration() {
        return (
            <>
                {addIdentifierDeclarationOrSupervisor && supervisorsAvailable && (
                    <FormGroup
                        label={translateKey('identifierDeclarationOrRegistration.howToAddIdentifierDeclaration')}
                    >
                        <RadioGroup
                            name="identifierDeclarationType"
                            onChange={async (event) =>
                                await changeIdentifierDeclarationType(event.target.value as IdentifierDeclarationType)
                            }
                            value={identifierDeclarationType}
                        >
                            <Radio
                                id="identifierDeclaration"
                                label={translateKey('identifierDeclarationOrRegistration.addNewIdentifierDeclaration')}
                                value="identifierDeclaration"
                            />
                            <Radio
                                disabled={!supervisorsAvailable}
                                id="selectSupervisor"
                                label={translateKey('personalSettings.selectSupervisor')}
                                value="selectSupervisor"
                            />
                        </RadioGroup>
                    </FormGroup>
                )}
                {((addIdentifierDeclarationOrSupervisor && identifierDeclarationType === 'identifierDeclaration') ||
                    addIdentifierDeclaration) && (
                    <IdentifierDeclaration
                        errors={props.errors}
                        identifierDeclarationCodeLabel={'identifierDeclarationOrRegistration.newIdentifierDeclaration'}
                        isRequired
                        onChange={async (key, value) => await handleChange(key, value as any)}
                        onValidateOne={async (name) => await props.onValidateOne(name)}
                        value={props.identifiers}
                    />
                )}

                {addIdentifierDeclarationOrSupervisor &&
                    supervisorsAvailable &&
                    identifierDeclarationType === 'selectSupervisor' && (
                        <SelectSupervisor
                            errors={props.errors}
                            onChangeSupervisor={async (value, label) => {
                                await handleChange('identifierDeclarationPractitionerId', value || null);
                                setSelectedSupervisorLabel(label || null);
                            }}
                            organizationId={props.organizationId}
                            practitionerId={props.practitionerId}
                            selectedSupervisor={props.identifiers.identifierDeclarationPractitionerId}
                            selectedSupervisorLabel={selectedSupervisorLabel}
                        />
                    )}
            </>
        );
    }

    function renderIdentifierRegistrationBig() {
        if (addIdentifierRegistrationBig) {
            return (
                <InputTextField
                    error={props.errors.get('identifierRegistrationBig')}
                    fieldKey={'identifierRegistrationBig'}
                    hasErrors={props.errors.has('identifierRegistrationBig')}
                    label={'identifierDeclarationOrRegistration.identifierRegistrationBig'}
                    placeholder={translateKey(
                        'personalSettings.addIdentifier',
                        translateKey('identifierDeclarationOrRegistration.identifierRegistrationBig')
                    )}
                />
            );
        }

        return null;
    }

    async function changeIdentifierDeclarationType(type: IdentifierDeclarationType) {
        setIdentifierDeclarationType(type);

        if (type === 'identifierDeclaration') {
            await props.onChange('identifierDeclarationPractitionerId', null);
        } else {
            await props.onChange('identifierDeclaration', null);
            await props.onChange('hasDeclarableQualification', null);
        }
    }

    async function handleChange<K extends keyof IRegistrationCodes>(name: K, value: IRegistrationCodes[K]) {
        await props.onChange(name, value);
    }

    async function handleSubmit() {
        await props.onSave();
    }

    async function loadSupervisors() {
        const supervisors = await getSupervisors(props.organizationId, props.practitionerId);
        setSupervisorsAvailable(supervisors.length > 0);
    }
}
